import { useState, type ReactNode } from 'react';
import { Button, Select, ListBox, Label, ProgressBar, Modal } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import type { TrackExportConfig } from '../types';

interface TrackExportDialogProps {
  open: boolean;
  onClose: () => void;
  onExport: (config: TrackExportConfig) => Promise<void>;
}

interface ExportSelectProps {
  label: string;
  value: string | number | null;
  onChange: (v: string | number | null) => void;
  isDisabled?: boolean;
  children: ReactNode;
}

// React Aria 的 Key 是 string | number，不含 bigint

function ExportSelect({ label, value, onChange, isDisabled, children }: ExportSelectProps) {
  return (
    <Select value={value} onChange={onChange} isDisabled={isDisabled} variant="secondary" fullWidth>
      <Label className="text-[11px] font-medium text-white/60 select-none">{label}</Label>
      <Select.Trigger className="h-8 rounded-md border border-white/10 bg-white/5 px-2 text-xs text-white hover:bg-white/10 data-[disabled]:opacity-40">
        <Select.Value />
        <Select.Indicator className="size-3.5 text-white/50" />
      </Select.Trigger>
      <Select.Popover className="min-w-[var(--trigger-width)] border border-white/10 bg-black/90 backdrop-blur-sm">
        <ListBox className="py-1">{children}</ListBox>
      </Select.Popover>
    </Select>
  );
}

export function TrackExportDialog({ open, onClose, onExport }: TrackExportDialogProps) {
  const { t } = useTranslation();
  const [fps, setFps] = useState<string | number | null>('30');
  const [resolution, setResolution] = useState<string | number | null>('1080p');
  const [codec, setCodec] = useState<string | number | null>('h264');
  const [quality, setQuality] = useState<string | number | null>('ultra');
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    if (!exporting) onClose();
  };

  const handleExport = async () => {
    setExporting(true);
    setProgress(0);
    setError(null);
    if (fps == null || resolution == null || codec == null || quality == null) return;
    try {
      await onExport({
        fps: Number(fps) as TrackExportConfig['fps'],
        resolution: String(resolution) as TrackExportConfig['resolution'],
        codec: String(codec) as TrackExportConfig['codec'],
        quality: String(quality) as TrackExportConfig['quality'],
        onProgress: setProgress,
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('raceTrack.exportFailed'));
    } finally {
      setExporting(false);
      setProgress(0);
    }
  };

  return (
    <Modal>
      <Modal.Backdrop
        isOpen={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) handleClose();
        }}
        isDismissable={!exporting}
        isKeyboardDismissDisabled={exporting}
        variant="blur"
        className="bg-black/60"
      >
        <Modal.Container placement="center" size="sm">
          <Modal.Dialog className="border border-white/10 bg-black/80 text-white">
            <Modal.CloseTrigger
              isDisabled={exporting}
              className="!bg-transparent !text-white/60 hover:!bg-white/10 hover:!text-white [&_svg]:size-4"
            />
            <Modal.Header>
              <Modal.Heading className="text-xs font-medium !text-white">
                {t('raceTrack.exportTitle')}
              </Modal.Heading>
            </Modal.Header>
            <Modal.Body className="flex flex-col gap-3">
              <ExportSelect
                label={t('raceTrack.resolution')}
                value={resolution}
                onChange={setResolution}
                isDisabled={exporting}
              >
                <ListBox.Item
                  id="square-1080"
                  textValue={t('raceTrack.resolutionSquare')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.resolutionSquare')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="1080p"
                  textValue={t('raceTrack.resolution1080p')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.resolution1080p')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="720p"
                  textValue={t('raceTrack.resolution720p')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.resolution720p')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="original"
                  textValue={t('raceTrack.resolutionOriginal')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.resolutionOriginal')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              </ExportSelect>

              <div className="flex gap-3">
                <ExportSelect
                  label={t('raceTrack.frameRate')}
                  value={fps}
                  onChange={setFps}
                  isDisabled={exporting}
                >
                  <ListBox.Item
                    id="24"
                    textValue={t('raceTrack.fps24')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.fps24')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item
                    id="30"
                    textValue={t('raceTrack.fps30')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.fps30')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item
                    id="60"
                    textValue={t('raceTrack.fps60')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.fps60')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ExportSelect>

                <ExportSelect
                  label={t('raceTrack.codec')}
                  value={codec}
                  onChange={setCodec}
                  isDisabled={exporting}
                >
                  <ListBox.Item
                    id="h264"
                    textValue={t('raceTrack.codecH264')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.codecH264')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item
                    id="vp9"
                    textValue={t('raceTrack.codecVp9')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.codecVp9')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                  <ListBox.Item
                    id="vp8"
                    textValue={t('raceTrack.codecVp8')}
                    className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                  >
                    {t('raceTrack.codecVp8')}
                    <ListBox.ItemIndicator />
                  </ListBox.Item>
                </ExportSelect>
              </div>

              <ExportSelect
                label={t('raceTrack.quality')}
                value={quality}
                onChange={setQuality}
                isDisabled={exporting}
              >
                <ListBox.Item
                  id="standard"
                  textValue={t('raceTrack.qualityStandard')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.qualityStandard')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="high"
                  textValue={t('raceTrack.qualityHigh')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.qualityHigh')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
                <ListBox.Item
                  id="ultra"
                  textValue={t('raceTrack.qualityUltra')}
                  className="px-3 py-1.5 text-xs text-white/75 hover:bg-white/10 hover:text-white data-[selected]:text-white"
                >
                  {t('raceTrack.qualityUltra')}
                  <ListBox.ItemIndicator />
                </ListBox.Item>
              </ExportSelect>

              {exporting && (
                <ProgressBar
                  value={progress * 100}
                  size="sm"
                  aria-label={t('raceTrack.exportProgress')}
                  className="mt-1"
                >
                  <div className="flex items-center justify-between text-[10px] text-white/50">
                    <span>{t('raceTrack.exporting')}</span>
                    <span>{Math.round(progress * 100)}%</span>
                  </div>
                  <ProgressBar.Track className="h-1 rounded-full bg-white/10">
                    <ProgressBar.Fill className="rounded-full bg-indigo-500 shadow-[0_0_4px_#6366f1]" />
                  </ProgressBar.Track>
                </ProgressBar>
              )}

              {error && (
                <div className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200">
                  {error}
                </div>
              )}
            </Modal.Body>
            <Modal.Footer className="flex items-center justify-end gap-2">
              <Button
                variant="ghost"
                size="sm"
                onPress={handleClose}
                isDisabled={exporting}
                className="text-white/75 hover:bg-white/10 hover:text-white"
              >
                {t('raceTrack.cancel')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onPress={() => void handleExport()}
                isDisabled={exporting}
                isPending={exporting}
                className="bg-indigo-500 font-semibold text-white hover:bg-indigo-400"
              >
                {exporting ? t('raceTrack.exporting') : t('raceTrack.exportVideo')}
              </Button>
            </Modal.Footer>
          </Modal.Dialog>
        </Modal.Container>
      </Modal.Backdrop>
    </Modal>
  );
}
